import React, { useEffect, useState } from "react";
import {
  Drawer, Spin, Select, Switch, Descriptions, Table, Tag, Button, Popconfirm, Form, InputNumber,
  DatePicker, Input, Alert, Avatar, Space, Divider, message,
} from "antd";
import moment from "moment";
import {
  BRAND, STATUS_LABELS, STATUS_COLORS, PENALTY_REASONS, PENALTY_SOURCES, EVALUATION_LABELS,
  BAND_COLORS, fmtInt, fmtDec, fmtUsd, fmtPeriod, fmtDay, teacherName, reviewerLabel, rateLabel, apiError,
} from "./utils";

const axios = require("axios").default;

const Section = ({ title, extra, children }) => (
  <div style={{ marginBottom: 22 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
      <b style={{ fontSize: 13, letterSpacing: 0.3, color: "#444" }}>{title}</b>
      {extra}
    </div>
    {children}
  </div>
);

const TeacherDrawer = ({ teacherId, days, managers, onClose, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [teacher, setTeacher] = useState(null);
  const [showPenaltyForm, setShowPenaltyForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rates, setRates] = useState({ hourly: null, idle: null });
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`payroll/teacher/${teacherId}`, { dates: days });
      setTeacher(data.teacher);
      // Seçili günlerin hepsinde aynı ücret varsa alanlar onunla dolar; farklıysa boş kalır
      const r = data.teacher.rates;
      setRates(r.length === 1 ? { hourly: r[0].hourly, idle: r[0].idle } : { hourly: null, idle: null });
    } catch (err) {
      message.error(apiError(err, "Eğitmen detayı alınamadı."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (teacherId) {
      setTeacher(null);
      setShowPenaltyForm(false);
      load();
    }
  }, [teacherId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Her değişiklikten sonra hem detay hem ana liste yenilenir
  const run = async (request, success) => {
    setBusy(true);
    try {
      await request();
      if (success) message.success(success);
      await load();
      onChanged();
      return true;
    } catch (err) {
      message.error(apiError(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = (data) => run(() => axios.put(`payroll/teacher/${teacherId}/settings`, data), "Kaydedildi.");

  const saveRates = () =>
    run(
      () => axios.put(`payroll/teacher/${teacherId}/rates`, { dates: days, hourly_rate: rates.hourly, idle_rate: rates.idle }),
      `Ücret seçili ${days.length} güne kaydedildi.`
    );

  const monthAction = (period, action, text) =>
    run(() => axios.post(`payroll/months/${teacherId}/${period}/${action}`), text);

  const addPenalty = async (values) => {
    const ok = await run(
      () =>
        axios.post(`payroll/teacher/${teacherId}/penalties`, {
          ...values,
          event_date: values.event_date.format("YYYY-MM-DD"),
        }),
      "Kesinti kaydedildi."
    );
    if (ok) {
      form.resetFields();
      setShowPenaltyForm(false);
    }
  };

  const t = teacher;
  const lockedPeriods = new Set((t?.monthly || []).filter((m) => m.status !== "draft").map((m) => m.period));

  const monthColumns = [
    { title: "Ay", dataIndex: "period", render: (p) => <span style={{ textTransform: "capitalize" }}>{fmtPeriod(p)}</span> },
    { title: "Durum", dataIndex: "status", render: (s) => <Tag color={STATUS_COLORS[s]}>{STATUS_LABELS[s]}</Tag> },
    { title: "Ücret (başarılı / boş)", dataIndex: "rates", render: (r) => `${rateLabel(r)} PHP/sa` },
    { title: "Kur", dataIndex: "fx", render: (fx) => (fx ? `1 USD = ${fmtDec(fx)}` : <Tag color="red">Kur yok</Tag>) },
    { title: "Brüt PHP", dataIndex: "gross", align: "right", render: fmtDec },
    { title: "Ceza", dataIndex: "penalty", align: "right", render: (v) => (v ? `−${fmtDec(v)}` : "0") },
    { title: "Net PHP", dataIndex: "net", align: "right", render: (v) => <b>{fmtDec(v)}</b> },
    { title: "USD", dataIndex: "usd", align: "right", render: (v) => <b>{fmtUsd(v)}</b> },
    {
      title: "",
      key: "action",
      render: (_, m) => (
        <Space size={4}>
          {m.status === "draft" && (
            <Popconfirm
              title={`${fmtPeriod(m.period)} bordrosu ayın tamamı için hesaplanıp kilitlensin mi?`}
              okText="Onayla"
              cancelText="Vazgeç"
              onConfirm={() => monthAction(m.period, "approve", "Maaş onaylandı; ücret ve kur kilitlendi.")}
            >
              <Button size="small" type="primary" disabled={!m.fx}>Onayla</Button>
            </Popconfirm>
          )}
          {m.status === "approved" && (
            <>
              <Button size="small" onClick={() => monthAction(m.period, "paid", "Ödendi olarak işaretlendi.")}>Ödendi</Button>
              <Popconfirm
                title="Maaş onayını kaldırmak hesaplamayı tekrar açar. Devam edilsin mi?"
                okText="Evet"
                cancelText="Hayır"
                onConfirm={() => monthAction(m.period, "unlock", "Bordro düzenlemeye açıldı.")}
              >
                <Button size="small" danger>Kilidi Aç</Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  const penaltyColumns = [
    { title: "Tarih", dataIndex: "day", render: fmtDay },
    {
      title: "Neden",
      dataIndex: "reason",
      render: (r, p) => (
        <>
          {PENALTY_REASONS[r]} {p.event_count > 1 && `× ${p.event_count}`}
          <div style={{ fontSize: 12, color: "#888" }}>
            {PENALTY_SOURCES[p.source]}
            {p.lesson_id && ` · Ders ${p.lesson_id}`}
            {p.note && ` · ${p.note}`}
          </div>
        </>
      ),
    },
    { title: "Tutar", dataIndex: "amount_php", align: "right", render: (v) => `${fmtDec(v)} PHP` },
    {
      title: "Durum",
      dataIndex: "status",
      render: (s, p) => {
        const locked = lockedPeriods.has(p.day.slice(0, 7));
        return (
          <Tag
            color={s === "approved" ? "green" : "default"}
            style={{ cursor: locked ? "default" : "pointer" }}
            onClick={() =>
              !locked &&
              run(() => axios.put(`payroll/penalties/${p.id}/status`, { status: s === "approved" ? "pending" : "approved" }))
            }
          >
            {s === "approved" ? "Onaylandı" : "Onay bekliyor"}
          </Tag>
        );
      },
    },
    {
      title: "",
      key: "del",
      render: (_, p) =>
        !lockedPeriods.has(p.day.slice(0, 7)) && (
          <Popconfirm title="Kesinti silinsin mi?" okText="Evet" cancelText="Hayır"
            onConfirm={() => run(() => axios.delete(`payroll/penalties/${p.id}`), "Kesinti silindi.")}>
            <Button size="small" type="link" danger>Sil</Button>
          </Popconfirm>
        ),
    },
  ];

  return (
    <Drawer
      visible={!!teacherId}
      onClose={onClose}
      width={Math.min(980, window.innerWidth)}
      title={
        t ? (
          <Space>
            <Avatar src={t.image || undefined} style={{ background: BRAND }}>{t.first_name?.[0]}</Avatar>
            <div>
              <div>{teacherName(t)}</div>
              <div style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>{t.email} · {t.country || "-"}</div>
            </div>
          </Space>
        ) : "Eğitmen maaş detayı"
      }
    >
      <Spin spinning={loading || busy}>
        {t && (
          <>
            <Section title="EĞİTMEN / MANAGER ATAMASI VE ÜCRET">
              <Space wrap size={24}>
                <div>
                  <div style={{ fontSize: 12, color: "#888" }}>Atanmış Manager</div>
                  <Select
                    style={{ width: 220 }}
                    value={t.manager_id}
                    onChange={(v) => saveSettings({ manager_id: v ?? null })}
                    allowClear
                    placeholder="Atanmamış"
                    options={managers.map((m) => ({ value: m.id, label: m.name }))}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "#888" }}>Deneme dersleri açılan slota dahil (anlık görüşmeler için kapalı kalmalı)</div>
                  <Switch checked={t.trial_inside} onChange={(v) => saveSettings({ trial_inside: v })} />
                </div>
              </Space>

              <div style={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 8, padding: 14, marginTop: 14 }}>
                <Space wrap align="end" size={16}>
                  <div>
                    <div style={{ fontSize: 12, color: "#888" }}>Başarılı ders saatlik ücreti</div>
                    <InputNumber
                      min={0.01}
                      step={10}
                      style={{ width: 170 }}
                      addonAfter="PHP / saat"
                      placeholder="Farklı ücretler"
                      value={rates.hourly}
                      onChange={(v) => setRates({ ...rates, hourly: v })}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#888" }}>Boş saatlik ücret</div>
                    <InputNumber
                      min={0}
                      step={5}
                      style={{ width: 170 }}
                      addonAfter="PHP / saat"
                      placeholder="Farklı ücretler"
                      value={rates.idle}
                      onChange={(v) => setRates({ ...rates, idle: v })}
                    />
                  </div>
                  <Popconfirm
                    title={`Bu ücret seçili ${days.length} güne kaydedilsin mi?`}
                    okText="Kaydet"
                    cancelText="Vazgeç"
                    onConfirm={saveRates}
                    disabled={!(rates.hourly > 0) || rates.idle === null || rates.idle === undefined || lockedPeriods.size > 0}
                  >
                    <Button
                      type="primary"
                      disabled={!(rates.hourly > 0) || rates.idle === null || rates.idle === undefined || lockedPeriods.size > 0}
                    >
                      Seçili {days.length} güne kaydet
                    </Button>
                  </Popconfirm>
                </Space>
                <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
                  Ücret, tarih seçiminde seçili olan günlere kaydedilir; bu günler tekrar seçildiğinde girilen ücret görünür.
                  Ücret girilmemiş günlerde 180 / 40 PHP kullanılır.
                  {t.rates.length > 1 && <> Seçili günlerde farklı ücretler var: <b>{rateLabel(t.rates)}</b>.</>}
                  {lockedPeriods.size > 0 && (
                    <div style={{ color: "#cf1322" }}>
                      Seçili günler onaylı bordro ayı içeriyor; ücret değiştirmek için önce o ayın kilidini açın.
                    </div>
                  )}
                </div>
              </div>
            </Section>

            <Section title="ÇALIŞMA VE REZERVASYON SÜRELERİ · SİSTEM VERİSİ">
              <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 4 }}>
                <Descriptions.Item label="Açılan slot (dk)">{fmtInt(t.totals.opened)}</Descriptions.Item>
                <Descriptions.Item label="Rezervasyon (dk)">{fmtInt(t.totals.booked)}</Descriptions.Item>
                <Descriptions.Item label="Başarılı ders (dk)">{fmtInt(t.totals.completed)}</Descriptions.Item>
                <Descriptions.Item label="Deneme dersi (dk)">{fmtInt(t.totals.trial)}</Descriptions.Item>
                <Descriptions.Item label="Boş süre (dk)">{fmtInt(t.totals.idle)}</Descriptions.Item>
                <Descriptions.Item label="Gerçekleşmeyen rez. (dk)">{fmtInt(t.totals.noshow)}</Descriptions.Item>
                <Descriptions.Item label="Doluluk">%{fmtDec(t.totals.utilization)}</Descriptions.Item>
                <Descriptions.Item label="Öğrenci iptali (adet)">{t.studentCancels}</Descriptions.Item>
              </Descriptions>
              <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
                Süreler ders sisteminden gelir ve burada değiştirilemez. Gerçekleşmeyen rezervasyon (Rezervasyon − Başarılı)
                öğretmenin kaçırdığı ders anlamına gelmez ve boş süreye dahildir. Deneme dersi, randevu dışındaki anlık görüşmelerden hesaplanır (aşağıda).
              </div>
            </Section>

            <Section title="AYLIK BORDRO">
              <Table rowKey="period" size="small" pagination={false} columns={monthColumns} dataSource={t.monthly} scroll={{ x: true }} />
              <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
                Tablo seçili günlere göre hesaplanır. "Onayla" ilgili ayın tamamını hesaplar; ücret, kur ve tutarlar kilitlenir.
                Onaylı aya kesinti eklenemez, değiştirilemez.
              </div>
            </Section>

            <Section title={`DENEME DERSLERİ · SİSTEM VERİSİ (${t.detail.trials.length} görüşme)`}>
              <Table
                rowKey="roomname"
                size="small"
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                dataSource={t.detail.trials}
                locale={{ emptyText: "Seçili günlerde deneme dersi yok" }}
                scroll={{ x: true }}
                columns={[
                  { title: "Tarih", dataIndex: "day", render: fmtDay },
                  { title: "Saat", key: "time", render: (_, r) => `${r.start_time} - ${r.end_time}` },
                  { title: "Öğrenci", key: "student", render: (_, r) => reviewerLabel(r) },
                  { title: "Süre (dk)", dataIndex: "duration", align: "right", render: fmtDec },
                  { title: "Hediye (dk)", dataIndex: "gift", align: "right", render: fmtInt },
                  {
                    title: "Sayılan (dk)",
                    dataIndex: "minutes",
                    align: "right",
                    render: (v, r) => (
                      <b style={{ color: r.duration > r.gift ? "#d46b08" : undefined }}>{fmtDec(v)}</b>
                    ),
                  },
                ]}
              />
              <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
                Öğretmenin cevap verdiği anlık görüşmeler: süre öğretmenin odaya katıldığı andan görüşmenin kapandığı ana kadar sayılır.
                Kapanış kaydı olmayan görüşmeler ve hediye dakikası olmayan öğrenciler sayılmaz; sayılan süre öğrencinin hediye dakikasını
                geçemez (turuncu = sınırlandı).
              </div>
            </Section>

            <Section
              title="MAAŞ KESİNTİLERİ"
              extra={<Button size="small" onClick={() => setShowPenaltyForm(!showPenaltyForm)}>＋ Ceza Ekle</Button>}
            >
              {showPenaltyForm && (
                <div style={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 8, padding: 14, marginBottom: 12 }}>
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={addPenalty}
                    initialValues={{ reason: "miss", amount_php: 0, event_count: 1, status: "approved", source: "teacher", event_date: moment(days[days.length - 1]) }}
                  >
                    <Space wrap align="start">
                      <Form.Item name="reason" label="Ceza nedeni" rules={[{ required: true }]}>
                        <Select style={{ width: 200 }} options={Object.entries(PENALTY_REASONS).map(([value, label]) => ({ value, label }))} />
                      </Form.Item>
                      <Form.Item name="amount_php" label="Kesinti tutarı (PHP)" rules={[{ required: true }]}>
                        <InputNumber min={0} step={10} style={{ width: 150 }} />
                      </Form.Item>
                      <Form.Item name="event_count" label="Olay adedi" rules={[{ required: true }]}>
                        <InputNumber min={1} precision={0} style={{ width: 100 }} />
                      </Form.Item>
                      <Form.Item name="event_date" label="Olay tarihi" rules={[{ required: true }]}>
                        <DatePicker format="DD.MM.YYYY" />
                      </Form.Item>
                      <Form.Item name="status" label="Onay durumu">
                        <Select style={{ width: 200 }} options={[
                          { value: "approved", label: "Onaylandı – maaştan düş" },
                          { value: "pending", label: "Onay bekliyor – düşme" },
                        ]} />
                      </Form.Item>
                      <Form.Item name="source" label="Kaynak / sorumluluk">
                        <Select style={{ width: 180 }} options={Object.entries(PENALTY_SOURCES).map(([value, label]) => ({ value, label }))} />
                      </Form.Item>
                      <Form.Item name="lesson_id" label="Rezervasyon / Ders ID (isteğe bağlı)">
                        <Input style={{ width: 200 }} maxLength={64} />
                      </Form.Item>
                      <Form.Item name="note" label="Açıklama">
                        <Input style={{ width: 280 }} />
                      </Form.Item>
                    </Space>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>
                      Yalnızca onaylanan ve öğretmen kaynaklı kayıtlar maaşa ve skora yansır. Tutar 0 PHP olabilir; aynı ders ID aynı neden için iki kez sayılmaz.
                    </div>
                    <Space>
                      <Button onClick={() => setShowPenaltyForm(false)}>Vazgeç</Button>
                      <Button type="primary" htmlType="submit">Cezayı Kaydet</Button>
                    </Space>
                  </Form>
                </div>
              )}
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                columns={penaltyColumns}
                dataSource={t.detail.penalties}
                locale={{ emptyText: "Seçili günlerde kesinti kaydı yok" }}
              />
            </Section>

            <Divider />

            <Section title="PERFORMANS ÖZETİ VE ÖĞRENCİ YORUMLARI · SALT OKUNUR">
              <Space wrap style={{ marginBottom: 10 }}>
                <Tag color={BAND_COLORS[t.perf.band]} style={{ fontSize: 14, padding: "4px 10px" }}>
                  Skor {fmtDec(t.perf.score)} / 100 · {t.perf.band}
                </Tag>
                {t.perf.provisional && <Tag>Geçici (az veri)</Tag>}
                <span style={{ color: "#666" }}>
                  İptal: {t.perf.cancels} · Kaçırılan: {t.perf.misses} · Doğrulanmış yorum: {t.perf.reviewCount}
                </span>
              </Space>
              {t.detail.reviews.length ? (
                t.detail.reviews.slice(0, 8).map((r) => (
                  <div key={r.id} style={{ borderBottom: "1px solid #f5f5f5", padding: "6px 0" }}>
                    <b>{EVALUATION_LABELS[r.evaluation]}</b>
                    <span style={{ color: "#888", fontSize: 12 }}> · {fmtDay(r.day)} · Sistemden alındı</span>
                    <div style={{ fontSize: 12, color: "#666" }}>{reviewerLabel(r)}</div>
                    {r.description && <div style={{ color: "#555" }}>{r.description}</div>}
                  </div>
                ))
              ) : (
                <Alert type="info" showIcon message="Seçili günlerde sistemden gelen öğrenci değerlendirmesi bulunmuyor." />
              )}
            </Section>
          </>
        )}
      </Spin>
    </Drawer>
  );
};

export default TeacherDrawer;
